/**
 * azure-functions/shared/vendorQuestions.cjs
 *
 * Canonical implementation — the base question library, tier vocabulary,
 * reassessment-cadence defaults, and the resolution logic that computes
 * the effective question set for a vendor. Required directly by Azure
 * Functions (anonymous vendor portal verify/submit, the reassessment
 * sweeper) and re-exported for the frontend by
 * src/constants/vendorAssessmentDefaults.js (data) and
 * src/utils/vendorQuestions.js (resolution logic) — there is exactly
 * one copy of this logic; do not fork it again.
 *
 * Base library + category override, filtered by the vendor's tier.
 *
 * The per-tenant customised library lives in state.vendorAssessmentConfig and is
 * read at request time; these defaults are only the fallback for tenants that
 * have never customised. Invariant VDR-B-003: nothing here deletes or rewrites
 * stored answers.
 */

const MIN_TIER_LEVEL = 1; // most critical
const MAX_TIER_LEVEL = 3; // least critical
const DEFAULT_TIER_LEVEL = MIN_TIER_LEVEL;
const TIER_LEVELS = [1, 2, 3];

// Reassessment cadence defaults — must mirror src/constants/vendorAssessmentDefaults.js
const DEFAULT_REASSESSMENT_FREQUENCY_MONTHS = 12;
const DEFAULT_REASSESSMENT_CADENCE = {
  warnBeforeDays: 60,
  openBeforeDays: 30,
  finalWarnBeforeDays: 2,
  overdueRiskDays: 7,
  // Days past due at which the engine raises a pending-archive PROPOSAL (a
  // human-confirmed Action Centre item — never an automated archive, VDR-B-006).
  archiveProposalDays: 30,
};

const DEFAULT_VENDOR_TIERS = [
  { level: 1, label: 'Tier 1 — Critical',  requiresAssessment: true },
  { level: 2, label: 'Tier 2 — Important', requiresAssessment: true },
  { level: 3, label: 'Tier 3 — Low',       requiresAssessment: true },
];

const TIER_ALIASES = {
  '1': 1, 'tier 1': 1, 'tier1': 1, 't1': 1, 'critical': 1, 'high': 1, 'strategic': 1,
  '2': 2, 'tier 2': 2, 'tier2': 2, 't2': 2, 'important': 2, 'medium': 2, 'operational': 2,
  '3': 3, 'tier 3': 3, 'tier3': 3, 't3': 3, 'low': 3, 'tactical': 3, 'commodity': 3,
};

const DEFAULT_VENDOR_SECTIONS = [
  { id: 'data', label: 'Data Protection', icon: '🔒', questions: [
    { id: 'dp1', text: 'Does the vendor have a documented data protection and privacy policy?', appliesToTier: 3 },
    { id: 'dp2', text: 'Is data encrypted at rest and in transit using industry-standard methods?', appliesToTier: 3 },
    { id: 'dp3', text: 'Is a Data Processing Agreement (DPA) or equivalent in place?', appliesToTier: 3 },
    { id: 'dp4', text: 'Does the vendor have GDPR / data privacy compliance measures?', appliesToTier: 3 },
    { id: 'dp5', text: 'Are data retention and secure deletion procedures documented and enforced?', appliesToTier: 3 },
  ] },
  { id: 'security', label: 'Security Controls', icon: '🛡', questions: [
    { id: 'sc1', text: 'Does the vendor hold ISO 27001, SOC 2, or equivalent security certification?', appliesToTier: 3 },
    { id: 'sc2', text: 'Does the vendor conduct regular penetration testing by qualified third parties?', appliesToTier: 3 },
    { id: 'sc3', text: 'Is multi-factor authentication (MFA) enforced for all privileged access?', appliesToTier: 3 },
    { id: 'sc4', text: 'Does the vendor have a vulnerability management and patching programme?', appliesToTier: 3 },
    { id: 'sc5', text: 'Is there continuous security monitoring (SIEM/SOC) in place?', appliesToTier: 3 },
  ] },
  { id: 'bcp', label: 'Business Continuity', icon: '⚙', questions: [
    { id: 'bc1', text: 'Does the vendor have a documented Business Continuity Plan (BCP)?', appliesToTier: 3 },
    { id: 'bc2', text: 'Are Disaster Recovery tests conducted at least annually?', appliesToTier: 3 },
    { id: 'bc3', text: 'Are Recovery Time Objectives (RTO) and Recovery Point Objectives (RPO) defined and tested?', appliesToTier: 3 },
    { id: 'bc4', text: 'Does the vendor operate redundant infrastructure across multiple availability zones or regions?', appliesToTier: 3 },
  ] },
  { id: 'compliance', label: 'Compliance', icon: '◉', questions: [
    { id: 'co1', text: 'Does the vendor hold relevant industry certifications (ISO, PCI-DSS, HIPAA, etc.)?', appliesToTier: 3 },
    { id: 'co2', text: 'Is the vendor subject to regular independent audits with results available to customers?', appliesToTier: 3 },
    { id: 'co3', text: 'Does the vendor maintain compliance with all applicable laws and regulations?', appliesToTier: 3 },
    { id: 'co4', text: 'Does the vendor provide evidence of compliance status on request (audit reports, certificates)?', appliesToTier: 3 },
  ] },
  { id: 'access', label: 'Access Management', icon: '◐', questions: [
    { id: 'am1', text: 'Is role-based access control (RBAC) implemented with least-privilege principles?', appliesToTier: 3 },
    { id: 'am2', text: 'Are access rights reviewed and recertified at least annually?', appliesToTier: 3 },
    { id: 'am3', text: 'Is strong authentication required for all user accounts?', appliesToTier: 3 },
    { id: 'am4', text: 'Is privileged access management (PAM) in place for administrative accounts?', appliesToTier: 3 },
  ] },
  { id: 'incident', label: 'Incident Response', icon: '△', questions: [
    { id: 'ir1', text: 'Does the vendor have a documented and tested incident response plan?', appliesToTier: 3 },
    { id: 'ir2', text: 'Are breach notification procedures defined with contractually committed timelines?', appliesToTier: 3 },
    { id: 'ir3', text: 'Has the vendor had no material security incidents in the past 12 months?', appliesToTier: 3 },
    { id: 'ir4', text: 'Does the vendor provide incident reports and post-mortems to affected customers?', appliesToTier: 3 },
  ] },
];

function cloneSections(sections) {
  return (sections || []).map(s => ({ ...s, questions: (s.questions || []).map(q => ({ ...q })) }));
}

function buildDefaultVendorAssessmentConfig() {
  return {
    version: 1,
    tiers: DEFAULT_VENDOR_TIERS.map(t => ({ ...t })),
    baseSections: cloneSections(DEFAULT_VENDOR_SECTIONS),
    categoryOverrides: {},
    defaultReassessmentFrequencyMonths: DEFAULT_REASSESSMENT_FREQUENCY_MONTHS,
    reassessmentCadence: { ...DEFAULT_REASSESSMENT_CADENCE },
  };
}

function positiveMonths(n, fallback) {
  return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
}
function nonNegDays(n, fallback) {
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : fallback;
}

/** Effective reassessment frequency (months): vendor override → tenant default → 12. */
function effectiveFrequencyMonths(config, vendor) {
  const v = vendor && vendor.assessmentFrequencyMonths;
  if (Number.isFinite(v) && v > 0) return Math.round(v);
  return positiveMonths(config && config.defaultReassessmentFrequencyMonths, DEFAULT_REASSESSMENT_FREQUENCY_MONTHS);
}

/** Tenant cadence offsets, each field falling back to the canonical default. */
function getReassessmentCadence(config) {
  const c = (config && config.reassessmentCadence) || {};
  return {
    warnBeforeDays:      nonNegDays(c.warnBeforeDays,      DEFAULT_REASSESSMENT_CADENCE.warnBeforeDays),
    openBeforeDays:      nonNegDays(c.openBeforeDays,      DEFAULT_REASSESSMENT_CADENCE.openBeforeDays),
    finalWarnBeforeDays: nonNegDays(c.finalWarnBeforeDays, DEFAULT_REASSESSMENT_CADENCE.finalWarnBeforeDays),
    overdueRiskDays:     nonNegDays(c.overdueRiskDays,     DEFAULT_REASSESSMENT_CADENCE.overdueRiskDays),
    archiveProposalDays: nonNegDays(c.archiveProposalDays, DEFAULT_REASSESSMENT_CADENCE.archiveProposalDays),
  };
}

/** Add whole months to an ISO date, returning a new ISO string (null on bad input). */
function addMonths(iso, months) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const out = new Date(d.getTime());
  out.setMonth(out.getMonth() + months);
  return out.toISOString();
}

/** Next due date = completion date (or now) + effective frequency (months). */
function computeNextAssessmentDate(config, vendor, fromIso) {
  return addMonths(fromIso || new Date().toISOString(), effectiveFrequencyMonths(config, vendor));
}

function clampLevel(n) {
  if (!Number.isFinite(n)) return DEFAULT_TIER_LEVEL;
  return Math.min(MAX_TIER_LEVEL, Math.max(MIN_TIER_LEVEL, Math.round(n)));
}

function tierLevelOf(vendor) {
  const raw = vendor && vendor.tier;
  if (raw == null || raw === '') return DEFAULT_TIER_LEVEL;
  if (typeof raw === 'number') return clampLevel(raw);
  const key = String(raw).trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(TIER_ALIASES, key)) return TIER_ALIASES[key];
  const m = key.match(/(\d+)/);
  if (m) return clampLevel(Number(m[1]));
  return DEFAULT_TIER_LEVEL;
}

function getAssessmentConfig(state) {
  const cfg = state && state.vendorAssessmentConfig;
  if (cfg && Array.isArray(cfg.baseSections)) return cfg;
  return buildDefaultVendorAssessmentConfig();
}

function questionAppliesToLevel(question, level) {
  const at = question && question.appliesToTier;
  if (at == null) return true;
  return clampLevel(at) >= level;
}

function mergeSections(baseSections, extraSections) {
  const out = (baseSections || []).map(s => ({ ...s, questions: (s.questions || []).map(q => ({ ...q })) }));
  const byId = new Map(out.map(s => [s.id, s]));
  for (const ex of extraSections || []) {
    const target = byId.get(ex.id);
    if (target) {
      const seen = new Map(target.questions.map(q => [q.id, q]));
      for (const q of ex.questions || []) seen.set(q.id, { ...q });
      target.questions = Array.from(seen.values());
    } else {
      const cloned = { ...ex, questions: (ex.questions || []).map(q => ({ ...q })) };
      out.push(cloned);
      byId.set(ex.id, cloned);
    }
  }
  return out;
}

/** Effective sections for a vendor — same result as the frontend getEffectiveSections. */
function getEffectiveSections(config, vendor) {
  const cfg = (config && Array.isArray(config.baseSections)) ? config : buildDefaultVendorAssessmentConfig();
  const level = tierLevelOf(vendor);
  const override = (cfg.categoryOverrides || {})[vendor && vendor.category];
  const merged = mergeSections(cfg.baseSections, override && override.extraSections);
  return merged
    .map(s => ({ ...s, questions: (s.questions || []).filter(q => questionAppliesToLevel(q, level)) }))
    .filter(s => s.questions.length > 0);
}

function getEffectiveQuestions(config, vendor) {
  return getEffectiveSections(config, vendor).flatMap(s => s.questions);
}

/** Look up a tier definition by level from a config. */
function getTierDef(config, level) {
  const tiers = (config && config.tiers) || [];
  return tiers.find(t => t.level === level) || null;
}

/** Does the given tier level require an assessment at all? Defaults to true. */
function tierRequiresAssessment(config, level) {
  const def = getTierDef(config, level);
  return def ? def.requiresAssessment !== false : true;
}

/** Does this vendor's tier require an assessment? */
function vendorRequiresAssessment(config, vendor) {
  return tierRequiresAssessment(config, tierLevelOf(vendor));
}

module.exports = {
  // Resolution logic
  buildDefaultVendorAssessmentConfig,
  getAssessmentConfig,
  getEffectiveSections,
  getEffectiveQuestions,
  tierLevelOf,
  getTierDef,
  tierRequiresAssessment,
  vendorRequiresAssessment,
  questionAppliesToLevel,
  effectiveFrequencyMonths,
  getReassessmentCadence,
  addMonths,
  computeNextAssessmentDate,
  // Seed data / vocabulary — the canonical defaults for
  // src/constants/vendorAssessmentDefaults.js
  DEFAULT_VENDOR_TIERS,
  DEFAULT_VENDOR_SECTIONS,
  TIER_ALIASES,
  TIER_LEVELS,
  MIN_TIER_LEVEL,
  MAX_TIER_LEVEL,
  DEFAULT_REASSESSMENT_FREQUENCY_MONTHS,
  DEFAULT_REASSESSMENT_CADENCE,
};
